import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel/TrackedCardCarousel'
import CruiseDestinationsSectionItem from './CruiseDestinationsSectionItem'
import TrackingContext from 'contexts/trackingContext'
import qs from 'qs'
import Heading from 'LuxKit/Typography/Heading'
import useCruiseInternationalMarket from 'hooks/useCruiseInternationalMarket'

type DestinationMessageKey =
  | 'sydney'
  | 'brisbane'
  | 'melbourne'
  | 'italy'
  | 'greece'
  | 'southPacific'
  | 'mediterranean'
  | 'northAmerica'
  | 'northernEurope'
  | 'latinAmerica'
  | 'caribbean'
  | 'ukGermanyFrance'

const destinationMessages = defineMessages({
  sydney: {
    id: 'HKyEl5bco1GPkhRN7C_1',
    defaultMessage: 'Sydney',
    description: 'Cruise destination tile label for Sydney on the cruises landing page',
  },
  brisbane: {
    id: 'qrk2crKxDEcK_YAhygtX',
    defaultMessage: 'Brisbane',
    description: 'Cruise destination tile label for Brisbane on the cruises landing page',
  },
  melbourne: {
    id: 'wNJQOJnAU1bZN4VQ8i9S',
    defaultMessage: 'Melbourne',
    description: 'Cruise destination tile label for Melbourne on the cruises landing page',
  },
  italy: {
    id: 'nb6w7YnX-y1aQfnNGCLG',
    defaultMessage: 'Italy',
    description: 'Cruise destination tile label for Italy on the cruises landing page',
  },
  greece: {
    id: '9_WaXGtLXdnfR0JP33tM',
    defaultMessage: 'Greece',
    description: 'Cruise destination tile label for Greece on the cruises landing page',
  },
  southPacific: {
    id: 'BPCWFW92NCgXXR0q14gt',
    defaultMessage: 'South Pacific',
    description: 'Cruise destination tile label for South Pacific on the cruises landing page',
  },
  mediterranean: {
    id: 'YntpQFtO-fS_y0nh-A2u',
    defaultMessage: 'Mediterranean',
    description: 'Cruise destination tile label for the Mediterranean on the cruises landing page',
  },
  northAmerica: {
    id: 'SqL7Gfy-v4TcuPUKj8E8',
    defaultMessage: 'North America',
    description: 'Cruise destination tile label for North America on the cruises landing page',
  },
  northernEurope: {
    id: 'FsuJCA6giPD1DJJj1RcC',
    defaultMessage: 'Northern Europe',
    description: 'Cruise destination tile label for Northern Europe on the cruises landing page',
  },
  latinAmerica: {
    id: '2mk5F5HHQqCdnAihCeXj',
    defaultMessage: 'Latin America',
    description: 'Cruise destination tile label for Latin America on the cruises landing page',
  },
  caribbean: {
    id: 'Bq2F_NC-wq8icK422z5m',
    defaultMessage: 'Caribbean',
    description: 'Cruise destination tile label for the Caribbean on the cruises landing page',
  },
  ukGermanyFrance: {
    id: 'YKXgWLHKexuJSFNOo4aY',
    defaultMessage: 'UK, Germany & France',
    description: 'Cruise destination tile label for the United Kingdom, Germany and France on the cruises landing page',
  },
})

type DestinationData = {
  destination: string
  displayNameKey: DestinationMessageKey
  image: { id: string }
  filter: string
}

const DESTINATION_DATA: Array<DestinationData> = [
  {
    destination: 'Sydney',
    displayNameKey: 'sydney',
    image: { id: 'v4kwli3fa6d30n2w1rk9' },
    filter: qs.stringify({
      destinationIds: 'le_acf06cdd9c744f969958e1f085554c8b',
      destinationNames: 'Sydney',
    }),
  },
  {
    destination: 'Brisbane',
    displayNameKey: 'brisbane',
    image: { id: 'e3shvrl7vnuw84mb3fbj' },
    filter: qs.stringify({
      destinationIds: 'le_67e103b0761e60683e83c559be18d40c',
      destinationNames: 'Brisbane',
    }),
  },
  {
    destination: 'Melbourne',
    displayNameKey: 'melbourne',
    image: { id: '3wgitcdr0mdfwtfa02ve' },
    filter: qs.stringify({
      destinationIds: 'le_bbaa9d6a1445eac881750bea6053f564',
      destinationNames: 'Melbourne',
    }),
  },
  {
    destination: 'Italy',
    displayNameKey: 'italy',
    image: { id: 'gyk1bjjmb974urlm3ay8' },
    filter: qs.stringify({
      destinationIds: 'le_93db85ed909c13838ff95ccfa94cebd9',
      destinationNames: 'Italy',
    }),
  },
  {
    destination: 'Greece',
    displayNameKey: 'greece',
    image: { id: 'la8fpc5ehwxlh9cyd9ne' },
    filter: qs.stringify({
      destinationIds: 'le_735b90b4568125ed6c3f678819b6e058',
      destinationNames: 'Greece',
    }),
  },
  {
    destination: 'South Pacific',
    displayNameKey: 'southPacific',
    image: { id: 'k9ms6hbvzrnln6o4oluc' },
    filter: qs.stringify({
      destinationIds: 'le_a8697c7e6e7988b29a9de094b8d554cc',
      destinationNames: 'South Pacific',
      cruiseLines: ['RCC', 'NCL', 'CEL'],
    }, { indices: false, arrayFormat: 'comma' }),
  },
]

const INTERNATIONAL_DESTINATION_DATA: Array<DestinationData> = [
  {
    destination: 'Mediterranean',
    displayNameKey: 'mediterranean',
    image: { id: 'deh51ookkm0egm5dy8w6' },
    filter: qs.stringify({
      destinationIds: 'le_93db85ed909c13838ff95ccfa94cebd9,le_d6eddba497017a4a5055ea593b073444,le_fa6fb501bac39d314fdc5a3d5a33c2e3,le_8af415d7ba15f25a61db66d54137d8d7',
      destinationNames: 'Mediterranean',
    }),
  },
  {
    destination: 'North America',
    displayNameKey: 'northAmerica',
    image: { id: 'p28suam4m7fnt7csh7' },
    filter: qs.stringify({
      destinationIds: 'le_cf874aad79e14b401a4c86954a596fa5',
      destinationNames: 'North America',
    }),
  },
  {
    destination: 'Northern Europe',
    displayNameKey: 'northernEurope',
    image: { id: 'edext8oubg3vyfjdj8d' },
    filter: qs.stringify({
      destinationIds: 'le_93d9ce9e859971d13fa6475ee1f6cb57',
      destinationNames: 'Northern Europe',
    }),
  },
  {
    destination: 'Latin America',
    displayNameKey: 'latinAmerica',
    image: { id: 'ad8j4yxoffd97g9jhj0q' },
    filter: qs.stringify({
      destinationIds: 'le_ded8e3f6fa8da10375ce4f89e1124893',
      destinationNames: 'Latin America',
    }),
  },
  {
    destination: 'Caribbean',
    displayNameKey: 'caribbean',
    image: { id: 'bihvmclzh6v5r5shea7' },
    filter: qs.stringify({
      destinationIds: 'le_8c34c92797a64e0dfe9c9cf718a277ad,le_ca9975a613d90f72ad5aff1b9aae502d,le_d7bce4d8dbac13945471c343c4973ba3',
      destinationNames: 'Caribbean',
    }),
  },
  {
    destination: 'UK, Germany & France',
    displayNameKey: 'ukGermanyFrance',
    image: { id: 'nnijsnnfnb21a1kaez1' },
    filter: qs.stringify({
      destinationIds: 'le_cfecdb276f634854f3ef915e2e980c31,le_ea5d2f1c4608232e07d3aa3d998e5135,le_093f65e080a295f8076b1c5722a46aa2',
      destinationNames: 'UK, Germany & France',
    }),
  },
]

function CruisePageDestinationTiles() {
  const intl = useIntl()
  const isInternationalMarket = useCruiseInternationalMarket()
  const destinationData = isInternationalMarket ? INTERNATIONAL_DESTINATION_DATA : DESTINATION_DATA

  const locations = destinationData.map((location) => ({
    ...location,
    name: intl.formatMessage(
      {
        id: 'MIDBHycAUBOWzyBt3C69',
        defaultMessage: '{destination} Cruises',
        description: 'Tile heading for cruises to a specific destination',
      },
      { destination: intl.formatMessage(destinationMessages[location.displayNameKey]) },
    ),
  }))

  const viewOffersLabel = intl.formatMessage({
    id: 'pBWBnTLrBfCsDhpRiekY',
    defaultMessage: 'View offers',
    description: 'Call to action button text to view cruise offers for a destination',
  })

  return (
    <TrackingContext.Provider
      value={{
        category: 'cruise_destinations',
        label: 'Cruise Page Destinations',
        listId: 'cruise-destinations-carousel',
        listName: 'Cruise Page Destinations',
      }}
    >
      <Group gap={32} direction="vertical">
        <LayoutContainer>
          <Heading variant="heading3">
            <FormattedMessage
              id="BZPJSyfsuwraI_lmSxRj"
              defaultMessage="Popular cruise <i>destinations</i>"
              description="Section heading for popular cruise destinations in the cruises section"
              values={{ i: (chunks) => <i>{chunks}</i> }}
            />
          </Heading>
        </LayoutContainer>

        <LayoutContainer>
          <TrackedCardCarousel pageSize={6} width={rem(180)}>
            {locations.map((location, index) => (
              <CruiseDestinationsSectionItem
                key={location.destination}
                destination={location}
                position={index}
                href={`/search/cruises?${location.filter}`}
                image={location.image}
                heading={location.name}
                subHeading=""
                align="top"
                callToAction={viewOffersLabel}
              />
            ))}
          </TrackedCardCarousel>
        </LayoutContainer>
      </Group>
    </TrackingContext.Provider>
  )
}

export default CruisePageDestinationTiles
